import { z } from '@customerio/hydra-validation';
import type {
  DataOutIntegration as UIAPIDataOutIntegration,
  IntegrationsDataOutUpdateFields,
} from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * A workspace's configured native data-out connector — the settings record
 * behind `/integrations/data_out/:type` (mixpanel, amplitude).
 *
 * Keyed on `identifier` (the connector slug), not the numeric `id`: the slug is
 * what the URL, the catalog row, and every endpoint path use, while `id` is the
 * internal integrations-table row id no caller addresses.
 *
 * `created_at` / `last_activity_at` stay raw unix seconds, matching the sibling
 * `integration` / `pipelines_connection` entities — the connections overview
 * already does its own sec→ms conversion. `events` is the one field normalized:
 * the backend omits it entirely for a connector with none selected, and every
 * consumer wants a list to map over.
 */
export type DataOutPersistedSettingValue = string | boolean | number;

export type DataOutIntegration = Omit<UIAPIDataOutIntegration, 'events' | 'settings'> & {
  events: string[];
  settings: Record<string, DataOutPersistedSettingValue>;
};

const DataOutPersistedSettingValueFromApi = z.union([z.string(), z.boolean(), z.number()]);

const DataOutSettingsFromApi = z
  .record(z.string(), z.unknown())
  .nullish()
  .transform((settings) => {
    const parsedSettings: Record<string, DataOutPersistedSettingValue> = {};
    for (const [key, value] of Object.entries(settings ?? {})) {
      const parsedValue = DataOutPersistedSettingValueFromApi.safeParse(value);
      if (parsedValue.success) parsedSettings[key] = parsedValue.data;
    }
    return parsedSettings;
  });

const DataOutIntegrationFromApi = z
  .looseObject({
    events: z
      .array(z.string())
      .nullish()
      .transform((events) => events ?? []),
    settings: DataOutSettingsFromApi,
  })
  .pipe(z.custom<DataOutIntegration>());

/**
 * The create/update body fields. Create and update publish structurally
 * identical shapes, and the backend requires all five on both, so one input
 * type serves both writes. Derived from the generated request type so the write
 * surface can't drift from the spec.
 */
export type DataOutIntegrationWriteInput = IntegrationsDataOutUpdateFields;

/** @public */
export const dataOutIntegration = defineEntity<
  DataOutIntegration,
  DataOutIntegrationWriteInput,
  void,
  undefined,
  'workspace'
>({
  name: 'data-out-integration',
  scope: 'workspace',
  pk: (d) => d.identifier,
  schema: { read: DataOutIntegrationFromApi },
});

export const dataOutIntegrationKeys = dataOutIntegration.keys;

export function parseDataOutIntegration(raw: UIAPIDataOutIntegration): DataOutIntegration {
  return dataOutIntegration.parse(raw);
}
